import type { RefObject } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Building2, Check, ChevronDown, MapPin } from "lucide-react";
import { cn } from "@/lib/utils";
import type { Tenant } from "@/types";

interface TenantSelectorProps {
  selectorRef: RefObject<HTMLDivElement | null>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenant: Tenant | undefined;
  tenantId: number | null;
  activeTenants: Tenant[];
  hasMultipleProperties: boolean;
  onSelectTenant: (tenantId: number) => void;
}

export function TenantSelector({
  selectorRef,
  open,
  onOpenChange,
  tenant,
  tenantId,
  activeTenants,
  hasMultipleProperties,
  onSelectTenant,
}: TenantSelectorProps) {
  return (
    <div className="relative" ref={selectorRef}>
      <button
        onClick={() => hasMultipleProperties && onOpenChange(!open)}
        aria-label="Selecionar propriedade para consultar dados"
        title="Troca somente o contexto dos dados; não entra na conta do cliente"
        className={cn(
          "flex items-center gap-2 px-2 md:px-3 h-11 rounded-xl",
          "bg-muted/30 border border-border/50",
          "text-sm transition-all duration-180 ease-[cubic-bezier(0.16,1,0.3,1)]",
          hasMultipleProperties
            ? "hover:bg-muted/50 hover:border-primary/30 cursor-pointer"
            : "cursor-default",
        )}
      >
        <Building2 className="w-4 h-4 text-violet-400 shrink-0" />
        <span className="hidden max-w-35 truncate font-medium lg:inline">
          {tenant?.name ?? "..."}
        </span>
        {tenant?.city ? (
          <span className="text-xs text-muted-foreground hidden sm:inline truncate max-w-20">
            - {tenant.city}
          </span>
        ) : null}
        {hasMultipleProperties ? (
          <ChevronDown
            className={cn(
              "w-3 h-3 text-muted-foreground shrink-0 transition-transform duration-180",
              open && "rotate-180",
            )}
          />
        ) : null}
      </button>

      <AnimatePresence>
        {open && hasMultipleProperties ? (
          <motion.div
            initial={{ opacity: 0, y: 6, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 6, scale: 0.97 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            className="absolute right-0 top-full mt-2 w-72 bg-card/85 backdrop-blur-xl border border-border/40 rounded-2xl shadow-2xl shadow-black/40 z-50"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-border/30">
              <span className="font-semibold text-sm">
                Contexto dos dados
              </span>
              <span className="text-xs text-muted-foreground bg-muted/50 px-2 py-0.5 rounded-full font-medium">
                {activeTenants.length} propriedades
              </span>
            </div>
            <div className="divide-y divide-border/20 max-h-64 overflow-y-auto">
              {activeTenants.map((item) => (
                <button
                  key={item.id}
                  onClick={() => onSelectTenant(item.id)}
                  className={cn(
                    "w-full flex items-center gap-3 px-4 py-3 hover:bg-primary/5 hover:translate-x-1 transition-all duration-180 ease-out text-left",
                    item.id === tenantId && "bg-primary/5 text-foreground",
                  )}
                >
                  <div
                    className={cn(
                      "p-2 rounded-xl shrink-0 transition-colors duration-180",
                      item.id === tenantId ? "bg-violet-500/20" : "bg-muted/30",
                    )}
                  >
                    <Building2
                      className={cn(
                        "w-4 h-4 transition-colors duration-180",
                        item.id === tenantId
                          ? "text-violet-400"
                          : "text-muted-foreground",
                      )}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{item.name}</p>
                    {item.city || item.state ? (
                      <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                        <MapPin className="w-2.5 h-2.5" />
                        {[item.city, item.state].filter(Boolean).join(", ")}
                      </p>
                    ) : null}
                    {item.id === tenantId ? (
                      <span className="flex items-center gap-1.5 text-2.5 text-violet-400 font-semibold uppercase tracking-wide mt-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-violet-400" />
                        Visualizando
                      </span>
                    ) : null}
                  </div>
                  {item.id === tenantId ? (
                    <Check className="w-4 h-4 text-violet-400 shrink-0" />
                  ) : null}
                </button>
              ))}
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
